import supabase from '@/supabaseClient'
import { Game, UserGame } from '@/types/database'
import { SteamGame, UserGameData } from './types'

interface PartialUserGame {
  id: string
  user_id: string
  game_id: string
  platforms: string[]
}

export const UserLibraryService = {
  async getUserLibrary(
    userId: string,
    steamGames: SteamGame[]
  ): Promise<Map<number, string[]>> {
    const { data, error } = await supabase
      .from('user_games')
      .select(
        `
        game_id,
        platforms,
        steam_appid,
        games!user_games_game_id_fkey (
          id,
          igdb_id,
          provider,
          title,
          steam_app_id
        )
      `
      )
      .eq('user_id', userId)

    if (error) throw error

    const romanMap: Record<string, string> = {
      i: '1',
      ii: '2',
      iii: '3',
      iv: '4',
      v: '5',
      vi: '6',
      vii: '7',
      viii: '8',
      ix: '9',
      x: '10',
    }

    const normalizeTitleForMatch = (value = '') => {
      let normalized = value
        .toLowerCase()
        .replace(/[™®©]/g, '')
        .replace(/[“”]/g, '"')
        .replace(/[‘’]/g, "'")
        .replace(/\s+/g, ' ')
        .trim()

      normalized = normalized.replace(
        /\b(i{1,3}|iv|v|vi|vii|viii|ix|x)\b/g,
        (match) => romanMap[match] || match
      )

      normalized = normalized
        .replace(/\s*[[{(].*?[\]})]\s*/g, ' ')
        .replace(/[^a-z0-9\s:'-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

      return normalized
    }

    const tokenize = (value = '') =>
      normalizeTitleForMatch(value)
        .split(/\s+/g)
        .filter(Boolean)

    const hasSequelMarker = (value = '') =>
      /\b(2|3|4|5|6|7|8|9|10|ii|iii|iv|v|vi|vii|viii|ix|x)\b/i.test(value)

    const hasEditionMarker = (value = '') =>
      /\b(remastered|definitive|complete|ultimate|collection|trilogy|bundle|deluxe|goty|edition)\b/i.test(
        value
      )

    const scoreCandidate = (
      queryTitle: string,
      candidate: {
        normalized: string
        tokens: Set<string>
        hasSequel: boolean
        hasEdition: boolean
      }
    ) => {
      const queryNormalized = normalizeTitleForMatch(queryTitle)
      if (queryNormalized && queryNormalized === candidate.normalized) {
        return 100
      }

      const queryTokens = new Set(tokenize(queryTitle))
      const intersection = new Set(
        [...queryTokens].filter((token) => candidate.tokens.has(token))
      )
      const unionSize = new Set([...queryTokens, ...candidate.tokens]).size || 1
      const jaccard = intersection.size / unionSize

      let score = Math.round(jaccard * 80)

      if (
        queryNormalized &&
        candidate.normalized &&
        (candidate.normalized.startsWith(queryNormalized) ||
          queryNormalized.startsWith(candidate.normalized))
      ) {
        score += 10
      }

      const queryHasSequel = hasSequelMarker(queryTitle)
      if (!queryHasSequel && candidate.hasSequel) score -= 20
      if (queryHasSequel && !candidate.hasSequel) score -= 15

      const queryHasEdition = hasEditionMarker(queryTitle)
      if (!queryHasEdition && candidate.hasEdition) score -= 10

      return Math.max(0, Math.min(100, score))
    }

    const gameMap = new Map<number, string[]>()
    const steamTitleMap = new Map<string, number>()
    const steamIgdbMap = new Map<number, number>()
    const steamIndex = steamGames.map((steamGame) => {
      const normalized = normalizeTitleForMatch(steamGame.name)
      const tokens = new Set(tokenize(steamGame.name))
      const entry = {
        appId: steamGame.appId,
        name: steamGame.name,
        normalized,
        tokens,
        hasSequel: hasSequelMarker(steamGame.name),
        hasEdition: hasEditionMarker(steamGame.name),
      }
      if (steamGame.igdb_id) {
        steamIgdbMap.set(Number(steamGame.igdb_id), steamGame.appId)
      }
      return entry
    })

    steamGames.forEach((steamGame) => {
      const normalizedSteamTitle = normalizeTitleForMatch(steamGame.name)
      steamTitleMap.set(normalizedSteamTitle, steamGame.appId)
      if (normalizedSteamTitle === 'sun haven') {
        console.info('[steam-library] steam title found', {
          steamTitle: steamGame.name,
          normalizedSteamTitle,
          appId: steamGame.appId,
        })
      }
    })

    data.forEach((item) => {
      let appId: number | null = null
      const game = Array.isArray(item.games)
        ? item.games[0]
        : item.games

      if (item.steam_appid) {
        appId = Number(item.steam_appid)
      } else if (game?.steam_app_id) {
        appId = Number(game.steam_app_id)
      }

      if (!appId && game?.igdb_id) {
        appId = steamIgdbMap.get(Number(game.igdb_id)) ?? null
      }

      if (!appId && game?.title) {
        const normalizedTitle = normalizeTitleForMatch(game.title)
        appId = steamTitleMap.get(normalizedTitle) ?? null
        if (normalizedTitle === 'sun haven') {
          console.info('[steam-library] match check', {
            title: game.title,
            normalizedTitle,
            appId,
          })
        }
        if (!appId) {
          const scored = steamIndex
            .map((candidate) => ({
              appId: candidate.appId,
              score: scoreCandidate(game.title, candidate),
            }))
            .sort((a, b) => b.score - a.score)
          const best = scored[0]
          const second = scored[1]
          const bestScore = best?.score ?? 0
          const gap = bestScore - (second?.score ?? 0)
          if (bestScore >= 90 && gap >= 5) {
            appId = best.appId
          }
        }
      }

      if (appId && !isNaN(appId)) {
        const existingPlatforms = gameMap.get(appId) || []
        gameMap.set(appId, [
          ...new Set([...existingPlatforms, ...(item.platforms || [])]),
        ])
      }
    })

    return gameMap
  },

  async linkGamesToUser(
    userId: string,
    insertedGames: Game[],
    // selectedGames: Set<string>
  ): Promise<void> {
    // Create user games data
    const userGamesData: UserGameData[] = insertedGames.map((game: Game) => {
      // For now, set default playtime to 0 as we need to refactor how playtime is passed
      // TODO: Pass Steam playtime data separately to maintain proper typing
      const playtime = 0

      return {
        user_id: userId,
        game_id: game.id,
        status: 'Owned',
        steam_playtime: playtime,
        platforms: ['Steam'],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        progress: 0,
      }
    })

    // Check for existing user games
    const { data: existingUserGames, error: checkError } = await supabase
      .from('user_games')
      .select('id, user_id, game_id, platforms')
      .in(
        'game_id',
        insertedGames.map((game) => game.id)
      )
      .eq('user_id', userId)

    if (checkError) throw checkError

    const newUserGames: UserGameData[] = []
    const existingGamesToUpdate: Partial<UserGame>[] = []

    // Separate new games from games that need platform updates
    for (const newGame of userGamesData) {
      const existingGame = existingUserGames?.find(
        (existing: PartialUserGame) =>
          existing.game_id === newGame.game_id &&
          existing.user_id === newGame.user_id
      )

      if (existingGame) {
        const currentPlatforms = existingGame.platforms || []
        const newPlatform = 'Steam'

        if (!currentPlatforms.includes(newPlatform)) {
          existingGamesToUpdate.push({
            id: existingGame.id,
            game_id: existingGame.game_id,
            platforms: [...currentPlatforms, newPlatform],
            updated_at: new Date().toISOString(),
          })
        }
      } else {
        newUserGames.push(newGame)
      }
    }

    // Insert new user games
    if (newUserGames.length > 0) {
      console.log('Attempting to upsert user_games:', newUserGames)
      const { data: upsertedUserGames, error } = await supabase
        .from('user_games')
        .upsert(newUserGames, { onConflict: 'user_id,game_id' })
      if (error) {
        console.error('Upsert error in user_games:', error)
        throw error
      }
      console.log('Upserted user_games result:', upsertedUserGames)
    }

    // Update existing games with new platforms
    if (existingGamesToUpdate.length > 0) {
      for (const game of existingGamesToUpdate) {
        const { error } = await supabase
          .from('user_games')
          .update({
            platforms: game.platforms,
            updated_at: new Date().toISOString(),
          })
          .eq('id', game.id)
          .eq('user_id', userId)

        if (error) throw error
      }
    }
  }
}
